import orderBy from "lodash/orderBy";

import { NO_REFUND_POLICY, formatCancellationPenalties } from "@holibob-packages/cancellation-policy";

import { ProductDataCancellationPolicyPenaltyListNode } from "../../../apiHooks/useProduct";
import { BulletedList } from "../../../components/BulletedList";
import { Typography } from "../../../components/Typography";
import { useLanguage, useNextTranslation } from "../../../hooks";
import { ListItem } from "../ListItem";
import { ProductSectionTitle } from "../ProductSectionTitle";
import { ProductSectionWrapper, ProductSectionWrapperProps } from "./ProductSectionWrapper";

type ProductCancellationPenaltySectionProps = Omit<ProductSectionWrapperProps, "children"> & {
    penaltyList: Pick<ProductDataCancellationPolicyPenaltyListNode, "formattedText" | "ordinalPosition">[];
};
export const ProductCancellationPenaltySection = ({
    penaltyList,
    ...props
}: ProductCancellationPenaltySectionProps) => {
    const [t] = useNextTranslation("product");

    return (
        <ProductSectionWrapper {...props} data-testid="product-cancellation-policy-section">
            <ProductSectionTitle data-ref="product-cancellation-policy-title">
                {t("label.cancellationPolicy")}
            </ProductSectionTitle>
            <ProductCancellationPenalties penaltyList={penaltyList} />
        </ProductSectionWrapper>
    );
};

type ProductCancellationPenaltiesProps = {
    penaltyList: Pick<ProductDataCancellationPolicyPenaltyListNode, "formattedText" | "ordinalPosition">[];
};
export const ProductCancellationPenalties = (props: ProductCancellationPenaltiesProps) => {
    const { penaltyList } = props;
    const [t] = useNextTranslation("product");
    const language = useLanguage();

    if (penaltyList.length === 0) {
        const cancellationPolicyList = formatCancellationPenalties([NO_REFUND_POLICY], language.code);
        return (
            <Typography color="subtle" size="large">
                {cancellationPolicyList[0].formattedText || t("cancellationPolicy.noRefund")}
            </Typography>
        );
    }

    if (penaltyList.length === 1) {
        return (
            <Typography color="subtle" size="large">
                {penaltyList[0].formattedText}
            </Typography>
        );
    }

    return (
        <BulletedList data-testid="product-cancellation-penalties">
            {orderBy(penaltyList, "ordinalPosition").map((penalty, idx) => (
                <ListItem key={idx}>
                    <Typography color="subtle" size="large">
                        {penalty.formattedText}
                    </Typography>
                </ListItem>
            ))}
        </BulletedList>
    );
};
